<template>
  <PreAuthPage class="firm" card-class="firm__card">
    <section class="sh-card__head">
      <LogoTile :branding="branding" :alt="tenantName" :size="48" />
      <h1 class="sh-card__title">
        {{ guide ? (guide.mode === "book" ? t("firmSetup.guideBookTitle") : t("firmSetup.guideApplyTitle")) : t("firmSetup.title") }}
      </h1>
      <p class="sh-card__subtitle">
        <template v-if="guide && guide.mode === 'book'">
          {{ t("firmSetup.guideBookBody", { firm: guide.firmName, programme: guide.programmeName }) }}
        </template>
        <template v-else-if="guide">
          {{ t("firmSetup.guideApplyBody", { firm: guide.firmName, programme: guide.programmeName }) }}
        </template>
        <template v-else>{{ t("firmSetup.subtitle", { name: tenantName }) }}</template>
      </p>
      <p v-if="email" class="sh-whoami">{{ t("common.signedInAs") }} <b>{{ email }}</b></p>
    </section>

    <template v-if="guide">
      <v-btn color="primary" size="large" block class="firm__submit" @click="session.continueFirmGuide()">
        {{ guide.mode === "book" ? t("firmSetup.guideBook") : t("firmSetup.guideApply") }}
      </v-btn>
    </template>

    <template v-else>
      <p v-if="error" class="sh-card__error" role="alert">
        <v-icon size="15">error_outline</v-icon>
        <span>{{ error }}</span>
      </p>
      <form class="firm__form" @submit.prevent="submit">
        <v-text-field
          v-model="firmName"
          :label="t('firmSetup.firmName')"
          :hint="t('firmSetup.firmNameHint')"
          persistent-hint
          variant="outlined"
          density="comfortable"
          autocomplete="organization"
          autofocus
          :disabled="busy"
        />
        <v-text-field
          v-model="contactName"
          :label="t('firmSetup.contactName')"
          variant="outlined"
          density="comfortable"
          autocomplete="name"
          :disabled="busy"
          hide-details
        />
        <v-text-field
          v-model="contactPhone"
          :label="t('firmSetup.contactPhone')"
          type="tel"
          variant="outlined"
          density="comfortable"
          autocomplete="tel"
          inputmode="tel"
          :disabled="busy"
          hide-details
        />
        <v-btn type="submit" color="primary" size="large" block class="firm__submit" :loading="busy" :disabled="!canSubmit">
          {{ t("firmSetup.submit") }}
        </v-btn>
      </form>
      <v-btn class="firm__switch" variant="text" size="small" block @click="session.signOut()">
        {{ t("auth.differentAccountSignIn") }}
      </v-btn>
    </template>
  </PreAuthPage>
</template>

<script lang="ts">
/**
 * INS-37 — "Set up your firm" for an inspector tenant app.
 *
 * Shown when a signed-in visitor has no organisation and is not redeeming an
 * invite. CREATED does not whitelist them: the guide sends them to apply,
 * unless the programme row says requesterAccess === "any". A not_found from
 * the API (flag off) moves the session back to today's orgless screen.
 */
import { computed, defineComponent, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useSessionStore } from "@/stores/session";
import LogoTile from "@/shell/LogoTile.vue";
import PreAuthPage from "@/shell/PreAuthPage.vue";

export default defineComponent({
  name: "FirmSetup",
  components: { LogoTile, PreAuthPage },
  setup() {
    const { t } = useI18n();
    const session = useSessionStore();
    const firmName = ref("");
    const contactName = ref("");
    const contactPhone = ref("");
    const busy = ref(false);
    const error = ref<string | null>(null);

    const canSubmit = computed(() => firmName.value.trim().length > 1 && !busy.value);
    const guide = computed(() => (session.status === "firm-guide" ? session.firmGuide : null));

    async function submit(): Promise<void> {
      if (!canSubmit.value) return;
      busy.value = true;
      error.value = null;
      try {
        await session.completeInspectorSignup({
          firmName: firmName.value.trim(),
          contactName: contactName.value.trim() || undefined,
          contactPhone: contactPhone.value.trim() || undefined,
        });
      } catch (e) {
        error.value = (e as Error)?.message?.replace(/^POST \S+ failed: \d+\s*/, "") || t("firmSetup.failed");
      } finally {
        busy.value = false;
      }
    }

    return {
      t,
      session,
      firmName,
      contactName,
      contactPhone,
      busy,
      error,
      canSubmit,
      guide,
      submit,
      tenantName: computed(() => session.tenant?.name || ""),
      branding: computed(() => session.tenant?.branding ?? null),
      email: computed(() => session.user?.email || null),
    };
  },
});
</script>

<style scoped>
.firm__form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.firm__submit {
  margin-top: 4px;
  font-weight: 650;
}
.firm__switch {
  margin-top: 8px;
  font-size: 12px;
  color: var(--sh-text-60);
}
</style>
